import React, {
  useState,
  useCallback,
} from 'react';
import { Button } from '@workboard/wobo-ui';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import { useTranslation } from 'react-i18next';

import * as MetricFunc from '../../../../../utils/metric';
import { krdetailConstants } from '../../../../../constants/krdetails.constants';
import { CascadeKrCreateModal, TEMP_ID_PREFIX } from './cascadeKrCreateModal';
import { CascadeKrList } from './cascadeKrList';
import {
  MetricObj,
  CascadeKrItem,
  User,
  AddOkrData,
} from './cascadeKrTypes';

export type CascadeKrListSectionProps = {
  cascadeList: CascadeKrItem[];
  metricObj: MetricObj;
  isOldDis: boolean;
  addOkrData: AddOkrData;
  realOwner: User;
  teamId: string;
  goalId: string;
  isError: boolean;
  renderError: (field: string) => React.ReactNode;
  onCascadeListChange: (
    newList: CascadeKrItem[],
    clearedErrors?: string[]
  ) => void;
  onValidationMessageChange: (message: string | null) => void;
  onShowDismissButtonChange?: (showDismissButton: boolean) => void;
};

export const CascadeKrListSection: React.FC<CascadeKrListSectionProps> = ({
  cascadeList,
  metricObj,
  isOldDis,
  addOkrData,
  realOwner,
  teamId,
  goalId,
  isError,
  renderError,
  onCascadeListChange,
  onValidationMessageChange,
  onShowDismissButtonChange,
}) => {
  const { t } = useTranslation();

  // Local state
  const [showCascadeKrCreateModal, setShowCascadeKrCreateModal] = useState(false);
  const [metricToEdit, setMetricToEdit] = useState<MetricObj | null>(null);

  // Event handlers
  const handleCreateCascadeKrClick = useCallback((open: boolean) => {
    setShowCascadeKrCreateModal(open);
  }, []);

  const handleDeleteCascadeKr = useCallback(
    (itemToDelete: CascadeKrItem) => {
      const newList = cascadeList.filter((u) => u.id !== itemToDelete.id);
      onCascadeListChange(newList, ['cascadeList']);
      onShowDismissButtonChange?.(false);
    },
    [cascadeList, onCascadeListChange, onShowDismissButtonChange],
  );
  const handleEditCascadeKr = useCallback(
    (editItem: CascadeKrItem) => {
      if (editItem.id?.startsWith(TEMP_ID_PREFIX)) {
        // reuse create cascade kr modal for new items
        setMetricToEdit({
          ...metricObj,
          ...editItem,
        });
      } else {
        // call EditMetric component to open edit modal
        const windowWithInit = window as unknown as {
          initEditSingleMetric: (
            gid: string, metricId?: string, options?: any, noGoalCheck?: boolean) => void;
        };
        windowWithInit.initEditSingleMetric(goalId, editItem.id, undefined, true);
      }
    },
    [goalId, metricObj],
  );

  const handleCascadeKrValidationClick = useCallback(
    (message: string) => {
      onValidationMessageChange(message);
    },
    [onValidationMessageChange],
  );

  const handleSaveCascadeKr = useCallback(
    (savedItem: MetricObj) => {
      const newCascadeList = [...cascadeList];
      const savedCascadeItem = savedItem as CascadeKrItem;
      if (metricToEdit) {
        const existingCascadeIndex = newCascadeList.findIndex(
          (u) => u.id === metricToEdit.id
        );
        if (existingCascadeIndex !== -1) {
          newCascadeList[existingCascadeIndex] = savedCascadeItem;
        }
      } else {
        newCascadeList.push(savedCascadeItem);
      }

      // Validate cascade KRs - currently out of scope
      // validateCascadeKrs(metricObj, cascadeList as unknown as MetricObj[], t);

      // Update parent and clear errors
      onCascadeListChange(newCascadeList, ['cascadeList']);

      // Close modal
      setMetricToEdit(null);
      setShowCascadeKrCreateModal(false);
    },
    [metricToEdit, cascadeList, onCascadeListChange],
  );

  return (
    <div className="cascade-kr-spec-wrapper">
      <div className="fieldcontent">
        <div className="labeltext-27px">{t('Cascades')}</div>
      </div>
      <div>
        <CascadeKrList
          metrics={cascadeList}
          onDelete={handleDeleteCascadeKr}
          onEdit={handleEditCascadeKr}
          onValidationClick={handleCascadeKrValidationClick}
        />
      </div>
      {isError ? renderError('cascadeList') : ''}
      <div>
        <Button
          style={{ padding: '4px 4px 4px 0px', fontSize: '14px', fontWeight: 700 }}
          kind="normal"
          onClick={() => {
            handleCreateCascadeKrClick(true);
          }}
          variant="ghost"
          className="krAlign-label"
          startIcon={
            <AddIcon
              style={{
                cursor: 'pointer',
                width: '18px',
                color: '#0072ab',
                fontWeight: 700,
                margin: '0px 0px 0px 4px',
              }}
            />
          }
        >
          {t('Cascade this Key Result')}
        </Button>
        {showCascadeKrCreateModal && (
          <CascadeKrCreateModal
            isOldDis={isOldDis}
            intervalOptions={MetricFunc.getIntervalOptions(
              'default',
              addOkrData.isEnabledBiweeklyKR,
              false,
              Number(metricObj.interval) === krdetailConstants.INTERVAL_BIQUARTERLY,
            )}
            metricObj={metricObj}
            realOwner={realOwner}
            teamId={teamId}
            onClose={() => handleCreateCascadeKrClick(false)}
            onSave={handleSaveCascadeKr}
          />
        )}
        {metricToEdit && (
          <CascadeKrCreateModal
            isOldDis={isOldDis}
            intervalOptions={MetricFunc.getIntervalOptions(
              'default',
              addOkrData.isEnabledBiweeklyKR,
              false,
              Number(metricObj.interval) === krdetailConstants.INTERVAL_BIQUARTERLY,
            )}
            metricObj={metricToEdit}
            realOwner={realOwner}
            teamId={teamId}
            onClose={() => setMetricToEdit(null)}
            onSave={handleSaveCascadeKr}
          />
        )}
      </div>
    </div>
  );
};
